import React from 'react';
import { Plane, Trash2, Clock, Save } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели с информацией об авиакомпании и сохраненными расчетами
 */
const AirlineInfoSidebar = ({ 
  airlineId, 
  airlinesData,
  savedCalculations,
  deleteSavedCalculation,
  loadSavedCalculation
}) => {
  // Получение данных выбранной авиакомпании
  const airline = airlinesData.find(a => a.id === airlineId) || airlinesData[0];

  return (
    <div className="space-y-6">
      {/* Секция информации об авиакомпании */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
            <Plane className="w-5 h-5 mr-2 text-purple-500" />
            {airline.name}
          </h3>
        </div>

        <div className="space-y-3">
          {/* Стоимость дополнительного багажа */}
          <div className="bg-gray-50 dark:bg-gray-700/30 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
              Стоимость дополнительного багажа
            </h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Внутренние рейсы:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {formatCurrency(airline.extraBaggageFees.domestic.extraPiece, 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Страны СНГ:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {formatCurrency(airline.extraBaggageFees.cis.extraPiece, 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Международные:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {formatCurrency(airline.extraBaggageFees.international.extraPiece, 0)}
                </span>
              </div>
            </div>
          </div>

          {/* Стоимость перевеса */}
          <div className="bg-gray-50 dark:bg-gray-700/30 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
              Стоимость перевеса багажа
            </h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Внутренние рейсы:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {formatCurrency(airline.extraBaggageFees.domestic.overweight.perKg, 0)}/кг
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Страны СНГ:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {formatCurrency(airline.extraBaggageFees.cis.overweight.perKg, 0)}/кг
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Международные:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {formatCurrency(airline.extraBaggageFees.international.overweight.perKg, 0)}/кг
                </span>
              </div>
            </div>
          </div>

          {/* Программа лояльности */}
          <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg border border-amber-100 dark:border-amber-800">
            <h4 className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-2">
              Программа лояльности
            </h4>
            <div className="space-y-1 text-sm">
              {Object.entries(airline.loyaltyProgram).map(([status, benefits]) => {
                if (status === 'standard' && benefits.extraWeight === 0 && benefits.extraCount === 0) {
                  return null; // Не отображаем стандартный статус без преимуществ
                }
                
                const statusNames = {
                  standard: 'Базовый',
                  silver: 'Серебряный',
                  gold: 'Золотой',
                  platinum: 'Платиновый'
                };
                
                return (
                  <div key={status} className="flex justify-between">
                    <span className="text-amber-600 dark:text-amber-400">{statusNames[status] || status}:</span>
                    <span className="font-medium text-amber-700 dark:text-amber-300">
                      {benefits.extraWeight > 0 ? `+${benefits.extraWeight} кг` : ''}
                      {benefits.extraWeight > 0 && benefits.extraCount > 0 ? ', ' : ''}
                      {benefits.extraCount > 0 ? `+${benefits.extraCount} место` : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Секция сохраненных расчетов */}
      {savedCalculations && savedCalculations.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
              <Save className="w-5 h-5 mr-2 text-purple-500" />
              Сохраненные расчеты
            </h3>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {savedCalculations.length} {pluralize(savedCalculations.length, 'расчет', 'расчета', 'расчетов')}
            </span>
          </div>

          <div className="space-y-3 max-h-64 overflow-y-auto pr-1 custom-scrollbar">
            {savedCalculations.map((calculation) => (
              <div 
                key={calculation.id} 
                className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                      {calculation.name}
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      {new Date(calculation.date).toLocaleDateString('ru-RU')}
                    </p>
                  </div>
                  
                  <div className="flex items-center space-x-1">
                    <button
                      title="Загрузить"
                      onClick={() => loadSavedCalculation(calculation.id)}
                      className="p-1 text-gray-500 hover:text-purple-600 dark:text-gray-400 dark:hover:text-purple-400"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                      </svg>
                    </button>
                    <button
                      title="Удалить"
                      onClick={() => deleteSavedCalculation(calculation.id)}
                      className="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                  <div className="flex justify-between mb-1">
                    <span>Авиакомпания:</span>
                    <span className="font-medium">
                      {airlinesData.find(a => a.id === calculation.formData.airline)?.name || calculation.formData.airline}
                    </span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span>Багаж:</span>
                    <span>
                      {calculation.formData.baggageWeight} кг × {calculation.formData.baggageCount} шт.
                    </span>
                  </div>
                  {calculation.results && calculation.results.totalExcessFee > 0 && (
                    <div className="flex justify-between mt-1 pt-1 border-t border-gray-200 dark:border-gray-600">
                      <span>Стоимость:</span>
                      <span className="font-semibold text-red-600 dark:text-red-400">
                        {formatCurrency(calculation.results.totalExcessFee, 0)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, few, many) => {
  const lastDigit = number % 10;
  const lastTwoDigits = number % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return many;
  if (lastDigit === 1) return one;
  if (lastDigit >= 2 && lastDigit <= 4) return few;
  return many;
};

export default AirlineInfoSidebar;